import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardTitle, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather'; // Import all icons
import {
  auditStatusGraphGet,
  auditTrendGet,
  topFiveAuditorGet,
  useAuditStatusGraphDetails,
  useAuditTrendDetails,
  auditorStatusGet,
  useAuditorStatus,
  useTopFiveAuditorDetails,
  lastFiveAuditorGet,
  useLastFiveAuditorDetails
} from '../../../store/slices/tenantSlices/auditorDashboardSlice';
import ChartComponent from '../../../components/charts/ChartComponent';
import AdminHeader from '../../../components/header/AdminHeader';
import { useUserDetails } from '../../../store/slices/authSlice';
import { useLoginUserType } from '../../../store/slices/commonSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';

const AuditorDashboard = () => {
  const dispatch = useDispatch();

  const user = useUserDetails();
  const loginUserType = useLoginUserType();
  const auditorStatus = useAuditorStatus();
  const auditTrendDetails = useAuditTrendDetails();
  const auditStatusGraphDetails = useAuditStatusGraphDetails();
  const topFiveAuditorDetails = useTopFiveAuditorDetails();
  const lastFiveAuditorDetails = useLastFiveAuditorDetails();

  const [processOptions, setProcessOptions] = useState([]);
  const [processId, setProcessId] = useState('');

  const [auditTrendDate, setAuditTrendDate] = useState(auditTrendDetails?.map((item) => item.date));
  const [recordCounts, setRecordCounts] = useState(
    auditTrendDetails?.map((item) => parseInt(item.total_audits))
  );

  const [auditStatusGraphLabels, setAuditStatusGraphLabels] = useState(['Passed', 'Failed']);
  const [auditStatusGraphSeries, setAuditStatusGraphSeries] = useState([
    Number(auditStatusGraphDetails?.passed_audit) || 0,
    Number(auditStatusGraphDetails?.failed_audit) || 0
  ]);

  const [topFiveListNames, setTopFiveListNames] = useState(
    topFiveAuditorDetails?.map((item) => item.username)
  );
  const [topFiveListCounts, setTopFiveListCounts] = useState(
    topFiveAuditorDetails?.map((item) => parseFloat(item.average_score))
  );

  const [lastFiveListNames, setLastFiveListNames] = useState(
    lastFiveAuditorDetails?.map((item) => item.username)
  );
  const [lastFiveListCounts, setLastFiveListCounts] = useState(
    lastFiveAuditorDetails?.map((item) => parseFloat(item.average_score))
  );

  const fetchAuditTrendDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(auditTrendGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchAuditStatusGraphDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(auditStatusGraphGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchTopFiveAuditorDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(topFiveAuditorGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchLastFiveAuditorDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(lastFiveAuditorGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchUserStatusDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(auditorStatusGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (loginUserType === 'auditor') {
      const options = user?.processIds.map((val) => ({
        label: val?.name,
        value: val?.id
      }));
      setProcessOptions(options);
    } else {
      getProcessOption();
    }
  }, [user?.processIds, loginUserType]);

  useEffect(() => {
    fetchAuditTrendDetails();
    const interval = setInterval(() => {
      fetchAuditTrendDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    fetchUserStatusDetails();
    const interval = setInterval(() => {
      fetchUserStatusDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    fetchAuditStatusGraphDetails();
    const interval = setInterval(() => {
      fetchAuditStatusGraphDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    fetchTopFiveAuditorDetails();
    const interval = setInterval(() => {
      fetchTopFiveAuditorDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    fetchLastFiveAuditorDetails();
    const interval = setInterval(() => {
      fetchLastFiveAuditorDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    setAuditTrendDate(auditTrendDetails?.map((item) => item.date));
    setRecordCounts(auditTrendDetails?.map((item) => parseInt(item.total_audits)));
  }, [auditTrendDetails]);

  useEffect(() => {
    setAuditStatusGraphLabels(['Passed', 'Failed']);
    setAuditStatusGraphSeries([
      Number(auditStatusGraphDetails?.passed_audit) || 0,
      Number(auditStatusGraphDetails?.failed_audit) || 0
    ]);
  }, [auditStatusGraphDetails]);

  useEffect(() => {
    setTopFiveListNames(topFiveAuditorDetails?.map((item) => item.username));
    setTopFiveListCounts(topFiveAuditorDetails?.map((item) => parseFloat(item.average_score)));
  }, [topFiveAuditorDetails]);

  useEffect(() => {
    setLastFiveListNames(lastFiveAuditorDetails?.map((item) => item.username));
    setLastFiveListCounts(lastFiveAuditorDetails?.map((item) => parseFloat(item.average_score)));
  }, [lastFiveAuditorDetails]);

  const totalAuditsSeries = useMemo(
    () => [
      {
        name: 'Total Audits',
        data: recordCounts
      }
    ],
    [recordCounts]
  );

  const totalAuditsOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        height: 200,
        toolbar: {
          show: false
        }
      },
      xaxis: {
        categories: auditTrendDate
      },
      stroke: {
        curve: 'smooth'
      },
      fill: {
        opacity: 0.4
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    }),
    [auditTrendDate]
  );

  const dispositionOptions = useMemo(
    () => ({
      chart: {
        type: 'pie',
        height: 200
      },
      labels: auditStatusGraphLabels,
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      ],
      fill: {
        opacity: 1
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const originalCount = opts.w.config.series[opts.seriesIndex];
          return `${originalCount}`;
        }
      },
      tooltip: {
        y: {
          formatter: (value) => `${value} calls`
        }
      }
    }),
    [auditStatusGraphLabels]
  );

  const listSeries = useMemo(
    () => [
      {
        name: 'List Count',
        data: topFiveListCounts
      }
    ],
    [topFiveListCounts]
  );

  const listOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 350
      },
      plotOptions: {
        bar: {
          horizontal: true
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => val
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: topFiveListNames
      },
      fill: {
        opacity: 1
      },
      legend: {
        show: false // no need for legend with single series
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    }),
    [topFiveListNames]
  );

  const listLastSeries = useMemo(
    () => [
      {
        name: 'List Count',
        data: lastFiveListCounts
      }
    ],
    [lastFiveListCounts]
  );

  const listLastOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 350
      },
      plotOptions: {
        bar: {
          horizontal: true
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => val
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: lastFiveListNames
      },
      fill: {
        opacity: 1
      },
      legend: {
        show: false // no need for legend with single series
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    }),
    [lastFiveListNames]
  );

  return (
    <React.Fragment>
      <AdminHeader
        title="Dashboard"
        isExpandSelect
        selectValue={processId}
        handelSelect={(e) => setProcessId(e.target.value)}
        options={[{ value: '', label: 'All' }, ...processOptions]}
      />

      <Row className="dashboard-card-container">
        <Col lg="2">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #007bff`, backgroundColor: `#007bff33` }}>
            <div className="card-icon" style={{ backgroundColor: `#007bff` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.FileText size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Total Audits</span>
              <span className="fs-3">{auditorStatus.total_audits || 0}</span>
            </div>
          </Card>
        </Col>

        <Col lg="2">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #28a745`, backgroundColor: `#28a74533` }}>
            <div className="card-icon" style={{ backgroundColor: `#28a745` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.CheckCircle size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Passed Audits</span>
              <span className="fs-3">{auditorStatus.passed_audit || 0}</span>
            </div>
          </Card>
        </Col>

        <Col lg="2">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #dc3545`, backgroundColor: `#dc354533` }}>
            <div className="card-icon" style={{ backgroundColor: `#dc3545` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.XCircle size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Failed Audits</span>
              <span className="fs-3">{auditorStatus.failed_audit || 0}</span>
            </div>
          </Card>
        </Col>

        <Col lg="2">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #17a2b8`, backgroundColor: `#17a2b833` }}>
            <div className="card-icon" style={{ backgroundColor: `#17a2b8` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.Percent size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Pass Percentage</span>
              <span className="fs-3">{auditorStatus.pass_percentage || '0%'}</span>
            </div>
          </Card>
        </Col>

        <Col lg="2">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #ffc107`, backgroundColor: `#ffc10733` }}>
            <div className="card-icon" style={{ backgroundColor: `#ffc107` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.Clock size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Pending Audits</span>
              <span className="fs-3">{auditorStatus.pending_audit || 0}</span>
            </div>
          </Card>
        </Col>

        <Col lg="2">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #6f42c1`, backgroundColor: `#6f42c133` }}>
            <div className="card-icon" style={{ backgroundColor: `#6f42c1` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.Users size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Agents Audited</span>
              <span className="fs-3">{auditorStatus.agents_audited || 0}</span>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="8">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Audit Trend"
            height={245}
            series={totalAuditsSeries}
            options={totalAuditsOptions}
            chartType="area"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Audit Trend</CardTitle>
              </div>
            }
          />
        </Col>
        <Col lg="4">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Pass vs Fail"
            height={245}
            series={auditStatusGraphSeries}
            options={dispositionOptions}
            chartType="pie"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Pass vs Fail</CardTitle>
              </div>
            }
          />
        </Col>
      </Row>
      <Row className="pt-3">
        <Col lg="6">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Top 5 Agents by Score"
            height={245}
            series={listSeries}
            options={listOptions}
            chartType="bar"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Top 5 Agents by Score</CardTitle>
              </div>
            }
          />
        </Col>
        <Col lg="6">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Low/bad 5 Agents by Score"
            height={245}
            series={listLastSeries}
            options={listLastOptions}
            chartType="bar"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Low/bad 5 Agents by Score</CardTitle>
              </div>
            }
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default AuditorDashboard;
